/**
 * Profile / Preferences Page
 * Shows the authenticated member their profile info (read-only) and lets
 * them update their personal preferences.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LocationColorBadge } from '@/components/ui/location-color-badge';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { changeMyPassword } from '@/lib/api/account';
import { updateMyPreferences } from '@/lib/api/preferences';
import { RSVP_SIZES, RSVP_SIZE_LABELS, isRSVPSizeCode, type RSVPSizeCode } from '@/types/units';

function resolveDefaultSize(value: unknown): RSVPSizeCode | null {
  return isRSVPSizeCode(value) ? value : null;
}

export function Profile() {
  const { user, setUser } = useAuthStore();
  const { showToast } = useUIStore();
  const [defaultSize, setDefaultSize] = useState<RSVPSizeCode | null>(null);
  const [mithaas, setMithaas] = useState<boolean>(true);
  const [autoRsvpWithDefault, setAutoRsvpWithDefault] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (user) {
      setDefaultSize(resolveDefaultSize(user.defaultSize));
      setMithaas(user.mithaas !== false);
      setAutoRsvpWithDefault(user.autoRsvpWithDefault === true);
    }
  }, [user?.defaultSize, user?.mithaas, user?.autoRsvpWithDefault]);

  if (!user) return null;

  const isDirty =
    resolveDefaultSize(user.defaultSize) !== defaultSize ||
    (user.mithaas !== false) !== mithaas ||
    (user.autoRsvpWithDefault === true) !== autoRsvpWithDefault;

  const resetPasswordForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handlePasswordModalChange = (open: boolean) => {
    setPasswordModalOpen(open);
    if (!open) resetPasswordForm();
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await updateMyPreferences({ defaultSize, mithaas, autoRsvpWithDefault });
      setUser(response.user);
      showToast('Preferences updated', 'success');
    } catch (error) {
      setDefaultSize(resolveDefaultSize(user.defaultSize));
      setMithaas(user.mithaas !== false);
      setAutoRsvpWithDefault(user.autoRsvpWithDefault === true);
      showToast(
        error instanceof Error ? error.message : 'Failed to update preferences',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (newPassword !== confirmPassword) {
      showToast("Passwords don't match", 'error');
      return;
    }

    try {
      setPasswordSaving(true);
      await changeMyPassword({ currentPassword, newPassword, confirmPassword });
      resetPasswordForm();
      setPasswordModalOpen(false);
      showToast('Password updated', 'success');
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to update password',
        'error'
      );
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-8 max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-1">Profile</h1>
        <p className="text-muted-foreground">
          Manage your account information and meal preferences.
        </p>
      </div>

      <Dialog open={passwordModalOpen} onOpenChange={handlePasswordModalChange}>
        <Card>
          <CardHeader>
            <CardTitle>Account</CardTitle>
            <CardDescription>Profile details on file. Contact an admin to update.</CardDescription>
            <CardAction>
              <DialogTrigger asChild>
                <Button type="button" variant="outline" size="sm">
                  Change password
                </Button>
              </DialogTrigger>
            </CardAction>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <ProfileRow label="Name" value={user.name} />
            <ProfileRow label="ITS ID" value={user.id} />
            {user.thaaliNumber && <ProfileRow label="Thaali #" value={user.thaaliNumber} />}
            {user.email && <ProfileRow label="Email" value={user.email} />}
            {user.phone && <ProfileRow label="Phone" value={user.phone} />}
            {user.pickupLocationName && (
              <ProfileRow
                label="Pickup Location"
                value={
                  <LocationColorBadge
                    colorCode={user.pickupLocationColorCode}
                    className="rounded-full px-2.5 font-medium"
                  >
                    {user.pickupLocationName}
                  </LocationColorBadge>
                }
              />
            )}
            <ProfileRow label="Role" value={<span className="capitalize">{user.role.toLowerCase()}</span>} />
          </CardContent>
        </Card>

        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
            <DialogDescription>
              Update the password you use to sign in with your ITS ID.
            </DialogDescription>
          </DialogHeader>

          <form className="space-y-4" onSubmit={handlePasswordSubmit}>
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current password</Label>
              <Input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                disabled={passwordSaving}
                autoComplete="current-password"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">New password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                disabled={passwordSaving}
                autoComplete="new-password"
                required
                minLength={8}
              />
              <p className="text-xs text-muted-foreground">
                Use at least 8 characters, one uppercase letter, and one number.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm new password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                disabled={passwordSaving}
                autoComplete="new-password"
                required
                minLength={8}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handlePasswordModalChange(false)}
                disabled={passwordSaving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={passwordSaving || !currentPassword || !newPassword || !confirmPassword}
              >
                {passwordSaving ? 'Updating...' : 'Update password'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle>Preferences</CardTitle>
          <CardDescription>
            These apply automatically to every thaali you receive, until you change them.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Default RSVP size</Label>
            <p className="text-xs text-muted-foreground">
              Choose the thaali size used by Apply to All and automatic RSVP.
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                type="button"
                variant={defaultSize === null ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setDefaultSize(null);
                  setAutoRsvpWithDefault(false);
                }}
                disabled={saving || autoRsvpWithDefault}
                aria-pressed={defaultSize === null}
              >
                Not set
              </Button>
              {RSVP_SIZES.map((size) => (
                <Button
                  key={size}
                  type="button"
                  variant={defaultSize === size ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDefaultSize(size)}
                  disabled={saving}
                  aria-pressed={defaultSize === size}
                >
                  {RSVP_SIZE_LABELS[size]} ({size})
                </Button>
              ))}
            </div>
            {autoRsvpWithDefault && (
              <p className="text-xs text-muted-foreground">
                Automatic RSVP is on, so a default size is required. Turn off automatic RSVP below to clear it.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Include Mithaas with my thaali?</Label>
            <p className="text-xs text-muted-foreground">
              Choose &ldquo;No&rdquo; to opt out of mithaas for all your future thaalis. You can change this any time.
            </p>
            <div className="flex gap-2 pt-1">
              <Button
                type="button"
                variant={mithaas ? 'default' : 'outline'}
                size="sm"
                onClick={() => setMithaas(true)}
                disabled={saving}
              >
                Yes
              </Button>
              <Button
                type="button"
                variant={!mithaas ? 'default' : 'outline'}
                size="sm"
                onClick={() => setMithaas(false)}
                disabled={saving}
              >
                No
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-start gap-1">
              <label
                htmlFor="autoRsvpWithDefault"
                className={`-mx-2 flex h-11 w-11 shrink-0 items-start justify-center pt-1 ${
                  saving || !defaultSize ? 'cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                <input
                  id="autoRsvpWithDefault"
                  type="checkbox"
                  checked={autoRsvpWithDefault}
                  onChange={(event) => setAutoRsvpWithDefault(event.target.checked)}
                  disabled={saving || !defaultSize}
                  className="h-6 w-6 shrink-0 rounded border-gray-300 lg:h-5 lg:w-5"
                />
              </label>
              <div className="space-y-1">
                <Label htmlFor="autoRsvpWithDefault" className="font-normal cursor-pointer">
                  Automatically RSVP me when a menu is published
                </Label>
                <p className="text-xs text-muted-foreground">
                  When enabled, newly published menus will RSVP you using your default size
                  {defaultSize ? ` (${defaultSize})` : ''}. You can still edit or skip an RSVP before cutoff.
                </p>
                {!defaultSize && (
                  <p className="text-xs text-amber-600">
                    Choose a default RSVP size before enabling automatic RSVP.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button onClick={handleSave} disabled={!isDirty || saving}>
              {saving ? 'Saving...' : 'Save preferences'}
            </Button>
            {isDirty && !saving && (
              <span className="text-xs text-muted-foreground">Unsaved changes</span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ProfileRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}
